import React, { useEffect, createContext, useContext } from "react"
import { useRouter } from "next/router"
import Script from "next/script"
import analytics, { AnalyticsAccountType } from "../../services/analytics"
import { useUserContext } from "../../context/UserContext"
import {clarity} from 'react-microsoft-clarity'

// Define the analytics context
interface AnalyticsContextType {
    trackEvent: (
        name: string,
        properties?: Record<string, any>,
        accountType?: AnalyticsAccountType,
        eventId?: string
    ) => void
    trackPageView: (
        url: string,
        accountType?: AnalyticsAccountType,
        eventId?: string,
        userProperties?: Record<string, any>
    ) => void
    getEventUserId: (eventId: string) => string | null
}

// Create context with default values
const AnalyticsContext = createContext<AnalyticsContextType>({
    trackEvent: () => {
        /* empty function by design */
    },
    trackPageView: () => {
        /* empty function by design */
    },
    getEventUserId: () => null,
})

// Hook to use analytics within components
export const useAnalytics = () => useContext(AnalyticsContext)

interface UnifiedAnalyticsProviderProps {
    children: React.ReactNode
}

const UnifiedAnalyticsProvider: React.FC<UnifiedAnalyticsProviderProps> = ({
    children,
}) => {
    const router = useRouter()
    const { user } = useUserContext()

    // Get current event ID from the URL
    const getCurrentEventId = (): string | undefined => {
        if (
            router.pathname.includes("/events/[eventid]") ||
            router.pathname.includes("/preview/events/[eventid]")
        ) {
            return router.query.eventid as string
        }

        // Handle special routes with hardcoded event IDs
        if (router.pathname.includes("guvali")) {
            return process.env.NEXT_PUBLIC_CURRENT_GUVALI_ID
        }

        if (router.pathname.includes("billory")) {
            return "66eaa9f10c3c0a69b8ab51c9"
        }

        return undefined
    }

    // Get the current event ID
    const currentEventId = getCurrentEventId()

    // Main pixel IDs
    const mainGoogleAnalyticsId = process.env.NEXT_PUBLIC_GA_TRACKING_ID
    const mainFacebookPixelId =
        process.env.NEXT_PUBLIC_PASSTICKETS_FACEBOOK_PIXEL_KEY
    const mainTikTokPixelId =
        process.env.NEXT_PUBLIC_PASSTICKETS_TIKTOK_PIXEL_KEY
    const mainClarityId = process.env.NEXT_PUBLIC_CLARITY_ID
    const mainPostHogId = process.env.NEXT_PUBLIC_POSTHOG_KEY
    const mainSnapchatPixelId =
        process.env.NEXT_PUBLIC_PASSTICKETS_SNAPCHAT_PIXEL_KEY

    const newGoogleAnalyticsId =
        process.env.NEXT_PUBLIC_PASSTICKETS_COM_ANALYTICS

    // Get event-specific pixel IDs
    const getEventSpecificPixels = (eventId: string | undefined) => {
        if (!eventId) return null

        // Define event-specific pixels configuration
        const eventPixels = {
            // Guvali event
            [process.env.NEXT_PUBLIC_CURRENT_GUVALI_ID]: {
                facebook: process.env.NEXT_PUBLIC_GUVALI_FACEBOOK_PIXEL_KEY,
                tiktok: process.env.NEXT_PUBLIC_GUVALI_TIKTOK_PIXEL_KEY,
                snapchat: process.env.NEXT_PUBLIC_GUVALI_SNAPCHAT_PIXEL_KEY,
            },
            // Ghabga event
            [process.env.NEXT_PUBLIC_GHABGA_EVENT_ID]: {
                facebook: process.env.NEXT_PUBLIC_GHABGA_FACEBOOK_PIXEL_KEY,
                tiktok: process.env.NEXT_PUBLIC_GHABGA_TIKTOK_PIXEL_KEY,
                snapchat: process.env.NEXT_PUBLIC_GHABGA_SNAPCHAT_PIXEL_KEY,
            },
            // Billory event
            "66eaa9f10c3c0a69b8ab51c9": {
                facebook: process.env.NEXT_PUBLIC_BILLORY_PIXEL_KEY,
                tiktok: process.env.NEXT_PUBLIC_BILLORY_TIKTOK_PIXEL_KEY,
                snapchat: process.env.NEXT_PUBLIC_BILLORY_SNAPCHAT_PIXEL_KEY,
            },
            // F1 event
            [process.env.NEXT_PUBLIC_CURRENT_F1_ID]: {
                tiktok: process.env.NEXT_PUBLIC_F1_TIKTOK_PIXEL_KEY,
                facebook: process.env.NEXT_PUBLIC_F1_FACEBOOK_PIXEL_KEY,
            },

            [process.env.NEXT_PUBLIC_EID_PARTY_2]: {
                snapchat:
                    process.env
                        .NEXT_PUBLIC_Eid_Nights_Ibrahim_AlHakami_SNAPCHAT,
            },

            [process.env.NEXT_PUBLIC_EID_PARTY_3]: {
                snapchat:
                    process.env
                        .NEXT_PUBLIC_Eid_Nights_Rehab_AlShumrani_SNAPCHAT,
            },
            [process.env.NEXT_PUBLIC_GUVALI_YACHT]: {
                snapchat: process.env.NEXT_PUBLIC_GUVALI_YACHT_SNAPCHAT,
            },
        }

        return eventPixels[eventId] || null
    }

    // Get pixels for current event
    const eventPixels = currentEventId
        ? getEventSpecificPixels(currentEventId)
        : null

    // Initialize analytics
    useEffect(() => {
        if (typeof window === "undefined") return

        // Initialize the analytics service
        analytics.initialize()

        // Initialize Microsoft Clarity
        if (mainClarityId) {
            clarity.init(mainClarityId)
        }

        // If user is logged in, identify them
        if (user && user.id) {
            analytics.identify({
                userId: user.id,
                traits: {
                    email: user.email || user.contact?.email,
                    name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                    firstName: user.first_name,
                    lastName: user.last_name,
                    phone: user.phone || user.contact?.phone,
                    gender: user.gender,
                },
            })
        }

        // Track router changes for page views
        const handleRouteChange = (url: string) => {
            // Main account tracking for all pages
            const userProperties = user
                ? {
                      userId: user.id,
                      userEmail: user.email || user.contact?.email,
                      userName:
                          `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                  }
                : {}

            analytics.pageView(url, "main", undefined, userProperties)

            // If on an event page, track with the event account too
            const eventId = getCurrentEventId()
            if (eventId) {
                analytics.pageView(url, "event", eventId, userProperties)
            }
        }

        // Listen for route changes
        router.events.on("routeChangeComplete", handleRouteChange)

        // Track initial page view
        handleRouteChange(router.asPath)

        // Cleanup
        return () => {
            router.events.off("routeChangeComplete", handleRouteChange)
        }
    }, [router.events, router.asPath, user, mainClarityId])

    // Context value with tracking functions
    const contextValue: AnalyticsContextType = {
        trackEvent: (name, properties = {}, accountType = "main", eventId) => {
            // Add user info to properties if available
            const userProperties = user
                ? {
                      userId: user.id,
                      userEmail: user.email || user.contact?.email,
                      userName:
                          `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                      ...properties,
                  }
                : properties

            analytics.track({
                name,
                properties: userProperties,
                accountType,
                eventId: eventId || currentEventId,
            })
        },

        trackPageView: (url, accountType = "main", eventId, userProperties) => {
            const userPropertiesToUse = user
                ? {
                      userId: user.id,
                      userEmail: user.email || user.contact?.email,
                      userName:
                          `${user.first_name || ""} ${user.last_name || ""}`.trim(),
                      ...(userProperties || {}),
                  }
                : userProperties || {}

            analytics.pageView(
                url,
                accountType,
                eventId || currentEventId,
                userPropertiesToUse
            )
        },

        getEventUserId: (eventId) => {
            return analytics.getEventUserId(eventId)
        },
    }

    return (
        <AnalyticsContext.Provider value={contextValue}>
            {/* Google Analytics */}
            {mainGoogleAnalyticsId && (
                <Script
                    src={`https://www.googletagmanager.com/gtag/js?id=${mainGoogleAnalyticsId}`}
                    strategy="afterInteractive"
                />
            )}
            {mainGoogleAnalyticsId && (
                <Script id="google-analytics" strategy="afterInteractive">
                    {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', '${mainGoogleAnalyticsId}');
          `}
                </Script>
            )}
            {newGoogleAnalyticsId && (
                <Script
                    src={`https://www.googletagmanager.com/gtag/js?id=${newGoogleAnalyticsId}`}
                    strategy="afterInteractive"
                />
            )}
            {newGoogleAnalyticsId && (
                <Script id="google-analytics" strategy="afterInteractive">
                    {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config1', '${newGoogleAnalyticsId}');
          `}
                </Script>
            )}

            {/* Facebook Pixel - Main */}
            {mainFacebookPixelId && (
                <Script id="facebook-pixel" strategy="afterInteractive">
                    {`
            !function(f,b,e,v,n,t,s)
            {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
            n.callMethod.apply(n,arguments):n.queue.push(arguments)};
            if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
            n.queue=[];t=b.createElement(e);t.async=!0;
            t.src=v;s=b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t,s)}(window, document,'script',
            'https://connect.facebook.net/en_US/fbevents.js');
            fbq('init', '${mainFacebookPixelId}');
            fbq('track', 'PageView');
          `}
                </Script>
            )}

            {/* Event-specific Facebook Pixel */}
            {eventPixels?.facebook && (
                <Script id="facebook-pixel-event" strategy="afterInteractive">
                    {`
            fbq('init', '${eventPixels.facebook}');
            fbq('track', 'PageView');
          `}
                </Script>
            )}

            {/* TikTok Pixel - Main */}
            {mainTikTokPixelId && (
                <Script id="tiktok-pixel" strategy="afterInteractive">
                    {`
            !function (w, d, t) {
              w.TiktokAnalyticsObject=t;
              var ttq=w[t]=w[t]||[];
              ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"];
              ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};
              for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);
              ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e};
              ttq.load=function(e,n){var i="https://analytics.tiktok.com/i18n/pixel/events.js";
              ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};
              var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;
              var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};
              ttq.load('${mainTikTokPixelId}');
              ttq.page();
            }(window, document, 'ttq');
          `}
                </Script>
            )}

            {/* Event-specific TikTok Pixel */}
            {eventPixels?.tiktok && (
                <Script id="tiktok-pixel-event" strategy="afterInteractive">
                    {`
            ttq.load('${eventPixels.tiktok}');
            ttq.page();
          `}
                </Script>
            )}

            {/* Main Snapchat Pixel */}
            {mainSnapchatPixelId && (
                <Script id="snapchat-pixel" strategy="afterInteractive">
                    {`
            (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
            {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
            a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
            r.src=n;var u=t.getElementsByTagName(s)[0];
            u.parentNode.insertBefore(r,u);})(window,document,
            'https://sc-static.net/scevent.min.js');
            snaptr('init', '${mainSnapchatPixelId}');
            snaptr('track', 'PAGE_VIEW');
          `}
                </Script>
            )}

            {/* Snapchat Pixel - Only for events that have it configured */}
            {eventPixels?.snapchat && (
                <Script id="snapchat-pixel" strategy="afterInteractive">
                    {`
            (function(e,t,n){if(e.snaptr)return;var a=e.snaptr=function()
            {a.handleRequest?a.handleRequest.apply(a,arguments):a.queue.push(arguments)};
            a.queue=[];var s='script';r=t.createElement(s);r.async=!0;
            r.src=n;var u=t.getElementsByTagName(s)[0];
            u.parentNode.insertBefore(r,u);})(window,document,
            'https://sc-static.net/scevent.min.js');
            snaptr('init', '${eventPixels.snapchat}');
            snaptr('track', 'PAGE_VIEW');
          `}
                </Script>
            )}

            {/* PostHog */}
            {mainPostHogId && (
                <Script id="posthog-script" strategy="afterInteractive">
                    {`
            !function(t,e){var o,n,p,r;e.__SV||(window.posthog=e,e._i=[],e.init=function(i,s,a){function g(t,e){var o=e.split(".");2==o.length&&(t=t[o[0]],e=o[1]),t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}}(p=t.createElement("script")).type="text/javascript",p.async=!0,p.src=s.api_host+"/static/array.js",(r=t.getElementsByTagName("script")[0]).parentNode.insertBefore(p,r);var u=e;for(void 0!==a?u=e[a]=[]:a="posthog",u.people=u.people||[],u.toString=function(t){var e="posthog";return"posthog"!==a&&(e+="."+a),t||(e+=" (stub)"),e},u.people.toString=function(){return u.toString(1)+".people (stub)"},o="capture identify alias people.set people.set_once set_config register register_once unregister opt_out_capturing has_opted_out_capturing opt_in_capturing reset isFeatureEnabled onFeatureFlags getFeatureFlag getFeatureFlagPayload reloadFeatureFlags group updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures getActiveMatchingSurveys getSurveys".split(" "),n=0;n<o.length;n++)g(u,o[n]);e._i.push([i,s,a])},e.__SV=1)}(document,window.posthog||[]);
            posthog.init('${mainPostHogId}', {api_host: '${process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com"}'});
          `}
                </Script>
            )}

            {children}
        </AnalyticsContext.Provider>
    )
}

export default UnifiedAnalyticsProvider
